import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useMutation, useQuery } from "convex/react";
import { useOrganization } from "@clerk/clerk-react";
import {
  COMPANY_SELECTION_STORAGE_KEY,
  companySelectionStorageWrite,
  companySelectionView,
  companyStoredSelectionValue,
} from "../domain/company";
import { api, type Doc, type Id } from "./api";

/**
 * The "current company" the app acts as.
 *
 *  - In Clerk mode it's derived from the active Clerk Organization, or a
 *    shared demo company when no organization is active (`ClerkCompanyProvider`).
 *  - In dev mode (no Clerk key) it's a localStorage-backed switcher over all
 *    companies (`DevCompanyProvider`).
 *
 * Both expose the same context so screens use `useCompany()` unchanged.
 */
type CompanyContextValue = {
  companyId: Id<"companies"> | null;
  company: Doc<"companies"> | null;
  companies: Doc<"companies">[];
  setCompanyId: (id: Id<"companies">) => void;
  status: "loading" | "ready" | "error";
  error: string | null;
  retry: () => void;
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function useCompany(): CompanyContextValue {
  const ctx = useContext(CompanyContext);
  if (ctx === null) throw new Error("useCompany must be used within a CompanyProvider");
  return ctx;
}

// --- Dev mode ---------------------------------------------------------------
export function DevCompanyProvider({ children }: { children: ReactNode }) {
  const rawCompanies = useQuery(api.companies.list);
  const companies = useMemo(() => rawCompanies ?? [], [rawCompanies]);
  const [stored, setStored] = useState<string | null>(() =>
    companyStoredSelectionValue(localStorage.getItem(COMPANY_SELECTION_STORAGE_KEY)),
  );

  const value = useMemo<CompanyContextValue>(() => {
    const selection = companySelectionView(companies, stored);
    return {
      companyId: selection.companyId,
      company: selection.company,
      companies,
      status: rawCompanies === undefined ? "loading" : "ready",
      error: null,
      retry: () => window.location.reload(),
      setCompanyId: (id) => {
        const write = companySelectionStorageWrite(id);
        localStorage.setItem(write.key, write.value);
        setStored(write.value);
      },
    };
  }, [companies, rawCompanies, stored]);

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

// --- Clerk mode -------------------------------------------------------------
export function ClerkCompanyProvider({ children }: { children: ReactNode }) {
  const { organization, isLoaded } = useOrganization();
  const ensureForOrg = useMutation(api.companies.ensureForOrg);
  const ensureDemo = useMutation(api.companies.ensureDemo);
  const ensureUser = useMutation(api.users.ensureCurrent);
  const [companyId, setCompanyId] = useState<Id<"companies"> | null>(null);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [provisionAttempt, setProvisionAttempt] = useState(0);
  const organizationId = organization?.id ?? null;
  const organizationName = organization?.name ?? "";

  useEffect(() => {
    let active = true;
    if (!isLoaded) return () => { active = false; };

    setCompanyId(null);
    setProvisionError(null);
    const provision = async () => {
      try {
        const cid = organizationId !== null
          ? await ensureForOrg({ clerkOrgId: organizationId, name: organizationName })
          : await ensureDemo({});
        // Provision the user row BEFORE exposing companyId, so company-scoped
        // queries (which authorize via the user row) don't race ahead of it.
        await ensureUser({ companyId: cid });
        if (active) setCompanyId(cid);
      } catch (error) {
        if (!active) return;
        setProvisionError(error instanceof Error ? error.message : "Could not prepare this company workspace.");
      }
    };

    void provision();
    return () => {
      active = false;
    };
  }, [ensureDemo, ensureForOrg, ensureUser, isLoaded, organizationId, organizationName, provisionAttempt]);

  const companyResult = useQuery(api.companies.get, companyId !== null ? { id: companyId } : "skip");
  const company = companyResult ?? null;
  const status: CompanyContextValue["status"] = provisionError !== null || companyResult === null
    ? "error"
    : !isLoaded || companyId === null || companyResult === undefined
      ? "loading"
      : "ready";
  const error = provisionError ?? (companyResult === null ? "The selected company could not be loaded." : null);

  const value = useMemo<CompanyContextValue>(
    () => ({
      companyId,
      company,
      companies: company ? [company] : [],
      setCompanyId: () => {},
      status,
      error,
      retry: () => setProvisionAttempt((attempt) => attempt + 1),
    }),
    [companyId, company, error, status],
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}
